import React, { useState, useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useSidebarContext } from '../context/SidebarContext';
import { HiMenu, HiBell, HiSun, HiMoon, HiLogout } from 'react-icons/hi';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

const AppHeader = () => {
  const { toggleSidebar, isSidebarOpen } = useSidebarContext();
  const { theme, toggleTheme } = useTheme();
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isNotificationsOpen) {
        const dropdowns = document.querySelectorAll('.dropdown-menu');
        let clickedInside = false;
        
        dropdowns.forEach(dropdown => {
          if (dropdown.contains(event.target)) {
            clickedInside = true;
          }
        });

        if (!clickedInside) {
          setIsNotificationsOpen(false);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isNotificationsOpen]);

  const handleLogout = async () => {
    try {
      await signOut();
      navigate('/login');
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  return (
    <header className="sticky top-0 z-50 flex w-full bg-white shadow-sm dark:bg-boxdark dark:drop-shadow-none lg:hidden">
      <div className="flex w-full items-center justify-between px-4 py-3 sm:px-6 md:px-8 min-h-[3.5rem]">
        {/* Left side - Logo and Menu */}
        <div className="flex items-center gap-2 sm:gap-4">
          {/* Hamburger Toggle BTN */}
          <button
            aria-controls="sidebar"
            onClick={(e) => {
              e.stopPropagation();
              toggleSidebar();
            }}
            className="z-50 block rounded-sm border border-gray-200 bg-white p-1.5 shadow-sm hover:bg-gray-100 dark:border-strokedark dark:bg-boxdark dark:hover:bg-gray-800 lg:hidden text-gray-700 dark:text-gray-200 transition-colors duration-200"
          >
            <HiMenu size={24} />
          </button>

          <RouterLink className="block flex-shrink-0" to="/">
            <div className="logo text-xl font-bold text-black dark:text-white">
              Sooot<span className="text-primary">AI</span>
            </div>
          </RouterLink>
        </div>

        {/* Right side - Actions */}
        <div className="flex items-center gap-3 2xsm:gap-7">
          <ul className="flex items-center gap-2 2xsm:gap-4">
            {/* Theme Toggle */}
            <li>
              <button
                onClick={toggleTheme}
                className="relative flex h-8.5 w-8.5 items-center justify-center rounded-full border-[0.5px] border-stroke bg-gray hover:text-primary dark:border-strokedark dark:bg-meta-4 dark:text-white"
              >
                {theme === 'dark' ? (
                  <HiSun className="h-5 w-5" />
                ) : (
                  <HiMoon className="h-5 w-5" />
                )}
              </button>
            </li>

            {/* Mobile Navigation - Only show when sidebar is closed */}
            <li className={`lg:hidden ${isSidebarOpen ? 'hidden' : 'block'}`}>
              <div className="flex items-center gap-2">
                {/* User Profile */}
                <div className="flex items-center gap-2">
                  <div className="h-8 w-8 rounded-full bg-[#6C63FF] flex items-center justify-center text-white font-medium">
                    {user?.email?.charAt(0).toUpperCase()}
                  </div>
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-200">
                    {user?.email?.split('@')[0]}
                  </span>
                </div>

                {/* Logout Button */}
                <button
                  onClick={handleLogout}
                  className="flex items-center gap-2 px-3 py-2 text-sm rounded-md text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800"
                >
                  <HiLogout className="h-5 w-5" />
                  <span>Logout</span>
                </button>
              </div>
            </li>

            {/* Desktop Navigation */}
            <li className="relative hidden lg:block">
              <button
                onClick={() => setIsNotificationsOpen(!isNotificationsOpen)}
                className="relative flex h-8.5 w-8.5 items-center justify-center rounded-full border-[0.5px] border-stroke bg-gray hover:text-primary dark:border-strokedark dark:bg-meta-4 dark:text-white"
              >
                <HiBell className="h-5 w-5" />
                <span className="absolute -top-0.5 -right-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-primary text-xs text-white">
                  3
                </span>
              </button>

              {/* Notifications Dropdown */}
              {isNotificationsOpen && (
                <div className="dropdown-menu absolute right-0 mt-2.5 flex h-90 w-75 flex-col rounded-sm border border-stroke bg-white shadow-default dark:border-strokedark dark:bg-boxdark sm:right-0 sm:w-80">
                  <div className="px-4.5 py-3">
                    <h5 className="text-sm font-medium text-bodydark2">Notifications</h5>
                  </div>

                  <div className="flex h-auto flex-col overflow-y-auto">
                    <div className="flex px-4.5 py-3 hover:bg-gray-2 dark:hover:bg-meta-4">
                      <div className="flex flex-1 items-center">
                        <div className="flex flex-col">
                          <p className="text-sm text-black dark:text-white">
                            New call received
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            2 minutes ago
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </li>
          </ul>
        </div>
      </div>
    </header>
  );
};

export default AppHeader;
